import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { isArray, sortBy, isEqual, isEmpty, isUndefined, isNull } from 'lodash'

import {
	Box,
	FormControl,
	List,
	ListItemButton,
	ListItemText,
	MenuItem,
	Select,
	Skeleton,
	Stack,
	TextField,
	Typography,
	Popover,
	Collapse,
	FormControlLabel,
	Checkbox,
} from '@mui/material'
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import { makeStyles } from '@mui/styles'

import CheckboxList from 'components/pages/dis-v2/_components/CheckboxList'
import LoadingSpinner from 'custom-components/LoadingSpinner'
import JobSummaryWidgit from './JobSummaryWidgit'

import { useMenu } from 'components/pages/dis-v2/contexts/MenuContext'
import { useJobSummaryViewReducer } from './helper/reducers/jobViewReducer'
import { useJobActivityViewQuery } from './helper/reducers/jobAcitivityViewQuery'
import { DISPLAY_COLUMNS } from './helper/constant'
import useProject from '../../hooks/useProject'

const DEFAULT_JOBID_FIELD_CAPTION = 'Job Id'

const useStyles = makeStyles((theme) => ({
	displayProgressBarCheckbox: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		'& .MuiFormControlLabel-root': {
			marginLeft: '0px !important',
		},
	},
}))

const NOT_SUPPORT_PROGRESS_BAR_COLUMN_TYPE = ['DATE', 'DATETIME', 'TIME', 'BOOLEAN']
function JobSummaryForm({ filterData, onFilterDataChange, isCreatingTile = false }) {
	const { t } = useTranslation('common')
	const environment = useSelector((state) => state.environment)
	const classes = useStyles()
	const { detailMenu } = useMenu()

	const [primaryKeyList, setPrimaryKeyList] = useState([])

	useEffect(() => {
		if (
			isNull(filterData?.jobIdFieldCaption) ||
			isUndefined(filterData?.jobIdFieldCaption) ||
			isEmpty(filterData?.jobIdFieldCaption)
		) {
			onFilterDataChange('jobIdFieldCaption', DEFAULT_JOBID_FIELD_CAPTION)
		}

		if (isNull(filterData?.showProgressBar) || isUndefined(filterData?.showProgressBar)) {
			onFilterDataChange('showProgressBar', true)
		}

		if (isNull(filterData?.showPercentage) || isUndefined(filterData?.showPercentage)) {
			onFilterDataChange('showPercentage', true)
		}
	}, [])

	const [loading, setLoading] = useState(false)

	const dispatch = useDispatch()
	const handleColumnsCheck = (value) => () => {
		const columnsChecked = filterData?.columnsChecked || []
		const currentIndex = columnsChecked.indexOf(value)
		const newChecked = [...columnsChecked]

		if (currentIndex === -1) {
			newChecked.push(value)
		} else {
			newChecked.splice(currentIndex, 1)
		}
		onFilterDataChange('columnsChecked', newChecked)
	}

	const { allColumns, viewLoading } = useJobSummaryViewReducer({
		formKey: filterData?.selectedJobForm?.formKey,
		projectKey: filterData?.selectedJobForm?.projectKey,
	})

	const { allColumns: taskAllColumns, viewLoading: taskViewLoading } = useJobActivityViewQuery({
		formKey: filterData?.selectedJobActivityForm?.formKey,
		projectKey: filterData?.selectedJobActivityForm?.projectKey,
	})

	const displayJobColumns = useMemo(() => {
		return allColumns.map((column) => ({
			...column,
			title: DISPLAY_COLUMNS[column.name] || column.title,
		}))
	}, [allColumns])

	const displayTaskColumns = useMemo(() => {
		return taskAllColumns.map((column) => ({
			...column,
			title: DISPLAY_COLUMNS[column.name] || column.title,
		}))
	}, [taskAllColumns])

	const { projectLoading } = useProject({ enabled: true })
	const handleSetPrimaryKey = (id) => {
		if (primaryKeyList.includes(id)) {
			onFilterDataChange(
				'primaryKeyList',
				primaryKeyList.filter((item) => item !== id)
			)
		} else {
			onFilterDataChange('primaryKeyList', [...primaryKeyList, id])
		}
	}

	const isShowDataNames = (filterData?.otherOptionsChecked || []).includes('showDataNames')

	const handleJobIdFieldChange = (e) => {
		const newValue = e.target.value
		onFilterDataChange('jobIdField', newValue)
		if (!primaryKeyList.includes(newValue)) {
			onFilterDataChange('primaryKeyList', [...primaryKeyList, newValue])
		}
	}

	const handleShowProgressBarChange = (e) => {
		onFilterDataChange('showProgressBar', !filterData?.showProgressBar ?? false)
	}

	const handleShowPercentageChange = (e) => {
		onFilterDataChange('showPercentage', !filterData?.showPercentage ?? false)
	}

	const currentSelectedNewJobForm = useMemo(() => {
		if (!filterData?.selectedNewJobForm && isCreatingTile) return {}

		return filterData?.selectedNewJobForm || filterData?.selectedJobForm
	}, [isCreatingTile, filterData?.selectedNewJobForm, filterData?.selectedJobForm])

	const currentSelectedNewTaskForm = useMemo(() => {
		if (!filterData?.selectedNewTaskForm && isCreatingTile) return {}

		return filterData?.selectedNewTaskForm || filterData?.selectedJobActivityForm
	}, [isCreatingTile, filterData?.selectedJobActivityForm, filterData?.selectedNewTaskForm])

	return (
		<Stack spacing={2}>
			<Box>
				<Typography variant="subtitle2" sx={{ pl: 1 }}>
					{t('common:misc.newJob')}
				</Typography>

				<ProjectFormSelectComponent
					initProjectForm={currentSelectedNewJobForm}
					onProjectFormChange={(value) => {
						onFilterDataChange('selectedNewJobForm', value)
					}}
				/>
			</Box>

			<Box>
				<Typography variant="subtitle2" sx={{ pl: 1 }}>
					{t('common:input.job')}
				</Typography>
				<ProjectFormSelectComponent
					initProjectForm={filterData?.selectedJobForm || {}}
					onProjectFormChange={(value) => onFilterDataChange('selectedJobForm', value)}
				/>
			</Box>

			<Box>
				<Typography variant="subtitle2" sx={{ pl: 1 }}>
					{t('common:input.customer')}
				</Typography>

				<ProjectFormSelectComponent
					initProjectForm={filterData?.selectedCustomerForm || {}}
					onProjectFormChange={(value) => onFilterDataChange('selectedCustomerForm', value)}
				/>
			</Box>

			<Box>
				<Typography variant="subtitle2" sx={{ pl: 1 }}>
					{t('common:input.jobTasks')}
				</Typography>

				<ProjectFormSelectComponent
					initProjectForm={filterData?.selectedJobActivityForm || {}}
					onProjectFormChange={(value) => {
						onFilterDataChange('selectedJobActivityForm', value)
						onFilterDataChange('columnsChecked', [])
					}}
				/>
			</Box>

			<Box>
				<Typography variant="subtitle2" sx={{ pl: 1 }}>
					{t('common:input.newTask')}
				</Typography>

				<ProjectFormSelectComponent
					initProjectForm={currentSelectedNewTaskForm}
					onProjectFormChange={(value) => {
						onFilterDataChange('selectedNewTaskForm', value)
					}}
				/>
			</Box>

			{!viewLoading ? (
				<CheckboxList
					title={t('common:dis.fieldToDisplay')}
					enableIcon
					onIconClick={handleSetPrimaryKey}
					checkedIconList={primaryKeyList}
					data={displayJobColumns}
					onItemClick={handleColumnsCheck}
					checkedList={filterData?.columnsChecked}
					getLabel={(option) => (isShowDataNames ? option.name : option.title)}
					getValue={(option) => option.name}
				/>
			) : (
				<Skeleton variant="rectangular" height={180} sx={{ mb: 1 }} />
			)}

			<div style={{ display: 'flex', alignItems: 'center' }}>
				<FormControl
					variant="outlined"
					size="small"
					sx={{ width: '40%', mr: '5px', minWidth: '50px' }}
				>
					<Typography variant="subtitle2" sx={{ pl: 1 }}>
						Job Id
					</Typography>
					<Select
						value={filterData?.jobIdField || ''}
						onChange={handleJobIdFieldChange}
						size="small"
						fullWidth
					>
						<MenuItem value="">
							<em>None</em>
						</MenuItem>
						{displayJobColumns.map((col) => (
							<MenuItem value={col.name}>{col.title}</MenuItem>
						))}
					</Select>
				</FormControl>

				<FormControl variant="outlined" size="small" fullWidth>
					<Typography variant="subtitle2" sx={{ pl: 1 }}>
						{t('common:input.caption')}
					</Typography>
					<TextField
						value={filterData?.jobIdFieldCaption || DEFAULT_JOBID_FIELD_CAPTION}
						onChange={(e) => {
							const newValue = e.target.value
							onFilterDataChange('jobIdFieldCaption', newValue)
						}}
						size="small"
						fullWidth
					/>
				</FormControl>
			</div>

			<div style={{ display: 'flex', alignItems: 'center' }}>
				<FormControl
					variant="outlined"
					size="small"
					sx={{ width: '25%', mr: '5px', minWidth: '80px' }}
				>
					<Typography variant="subtitle2" sx={{ pl: 1 }}>
						{t('common:input.filterField')}
					</Typography>
					<Select
						value={filterData?.filterField || ''}
						onChange={(e) => {
							const newValue = e.target.value
							onFilterDataChange('filterField', newValue)
						}}
						size="small"
					>
						<MenuItem value="">
							<em>None</em>
						</MenuItem>
						{displayJobColumns.map((col) => (
							<MenuItem value={col.name}>{col.title}</MenuItem>
						))}
					</Select>
				</FormControl>

				<FormControl ariant="outlined" size="small" sx={{ width: '30%', minWidth: '100px' }}>
					<Typography variant="subtitle2" sx={{ pl: 1 }}>
						{t('common:input.progressField')}
					</Typography>
					<Select
						value={filterData?.progressBarField || ''}
						onChange={(e) => {
							const newValue = e.target.value
							onFilterDataChange('progressBarField', newValue)
						}}
						size="small"
						fullWidth
					>
						<MenuItem value="">
							<em>None</em>
						</MenuItem>
						{displayJobColumns
							.filter((col) => !NOT_SUPPORT_PROGRESS_BAR_COLUMN_TYPE.includes(col.type))
							.map((col) => (
								<MenuItem value={col.name}>{col.title}</MenuItem>
							))}
					</Select>
				</FormControl>

				<FormControl size="small" sx={{ width: '45%' }}>
					<Typography variant="subtitle2" sx={{ pl: 1 }}>
						{t('common:input.display')}
					</Typography>
					<div className={classes.displayProgressBarCheckbox}>
						<FormControlLabel
							sx={{
								height: '40px',
								minWidth: '130px',

								'& .MuiFormControlLabel-label': {
									fontSize: '0.875rem !important',
								},
							}}
							control={
								<Checkbox
									checked={filterData?.showProgressBar}
									onChange={handleShowProgressBarChange}
									defaultChecked={true}
								/>
							}
							label={'Progress bar'}
						/>

						<FormControlLabel
							sx={{
								'& .MuiFormControlLabel-label': {
									fontSize: '0.875rem !important',
								},
							}}
							control={
								<Checkbox
									checked={filterData?.showPercentage}
									onChange={handleShowPercentageChange}
									defaultChecked={true}
								/>
							}
							label={'Percentage'}
						/>
					</div>
				</FormControl>
			</div>

			<JobSummaryWidgit
				summaryWidget={filterData?.summaryWidgets || []}
				onWidgetItemChange={(val) => onFilterDataChange('summaryWidgets', val)}
				title={`Job ${t('common:input.summaryWidget')}`}
				statusField={filterData?.statusField}
				onStatusFieldChange={(newValue) => {
					onFilterDataChange('statusField', newValue)
					if (!newValue) {
						onFilterDataChange('summaryWidgets', [])
					}
				}}
				allColumns={displayJobColumns}
				hasModalDashboardSetting={true}
				modalDashboardSetting={filterData?.modalDashboardSetting}
				onModalDashboardSettingChange={(value) => {
					onFilterDataChange('modalDashboardSetting', value)
				}}
			/>
			<JobSummaryWidgit
				summaryWidget={filterData?.taskSummaryWidgets || []}
				onWidgetItemChange={(val) => onFilterDataChange('taskSummaryWidgets', val)}
				title={`Task ${t('common:input.summaryWidget')}`}
				statusField={filterData?.taskStatusField}
				onStatusFieldChange={(newValue) => {
					onFilterDataChange('taskStatusField', newValue)
					if (!newValue) {
						onFilterDataChange('taskSummaryWidgets', [])
					}
				}}
				allColumns={displayTaskColumns}
			/>
		</Stack>
	)
}

export function ProjectFormSelectComponent(props) {
	const { initProjectForm, onProjectFormChange } = props
	const [t] = useTranslation('common')
	const { environment } = useSelector((state) => state)

	const { projectLoading } = useProject({ enabled: true })
	const [projectFormInfo, setProjectFormInfo] = useState({})
	const displayValue = useMemo(() => {
		if (!projectFormInfo || isEmpty(projectFormInfo)) return ''

		return projectFormInfo?.projectName + ' / ' + projectFormInfo?.formName
	}, [projectFormInfo])

	const [anchorEl, setAnchorEl] = useState(null)
	const isSelectOpen = useMemo(() => {
		return Boolean(anchorEl)
	}, [anchorEl])
	const handleSelectOpen = (event) => {
		setAnchorEl(event.currentTarget)
	}
	const handleSelectClose = () => {
		setAnchorEl(null)
	}

	useEffect(() => {
		if (isEqual(initProjectForm, projectFormInfo)) return

		setProjectFormInfo(initProjectForm)
	}, [initProjectForm])

	const handleFormSelection = (project, form) => {
		const projectKey = project.key
		const formKey = form.key

		onProjectFormChange({
			projectKey: projectKey,
			formKey: formKey,
			formName: form.name,
			projectName: project.name,
		})
		handleSelectClose()
	}

	const [collapseProjects, setCollapseProjects] = useState([])
	const handleCollapsed = (projectKey) => {
		const found = collapseProjects.includes(projectKey)
		if (!found) {
			setCollapseProjects([...collapseProjects, projectKey])
		} else {
			setCollapseProjects(collapseProjects.filter((item) => item !== projectKey))
		}
	}

	const allProjects = useMemo(() => {
		const a = [...environment?.projects, ...environment?.lookups]
		return a
	}, [environment])

	return (
		<Box spacing={2}>
			<TextField
				id="select-default-form"
				size="small"
				onClick={handleSelectOpen}
				value={displayValue}
				fullWidth
				InputProps={{
					readOnly: true,
					endAdornment: isSelectOpen ? (
						<>
							{environment?.isLoadingProject ? (
								<LoadingSpinner withStyle={false} size={15} />
							) : (
								<ArrowDropUp position="end" />
							)}
						</>
					) : (
						<>
							{environment?.isLoadingProject ? (
								<LoadingSpinner withStyle={false} size={15} />
							) : (
								<ArrowDropDown position="end" />
							)}
						</>
					),
				}}
			/>

			<Popover
				anchorEl={anchorEl}
				id={'my-default-form-container'}
				//keepMounted
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				open={isSelectOpen}
				onClose={handleSelectClose}
				slotProps={{ paper: { style: { width: '400px', maxWidth: '45vw', maxHeight: '60vh' } } }}
			>
				<List
					component="nav"
					dense={true}
					aria-labelledby="nested-list-subheader"
					onMouseDown={(event) => event.stopPropagation()}
				>
					{allProjects?.map((project, index) => (
						<div key={project.key}>
							<ListItemButton
								sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}
								onClick={() => handleCollapsed(project.key)}
							>
								<ListItemText>
									<Typography variant={'h6'} fontSize={16}>
										{project.name}
									</Typography>
								</ListItemText>
								{!collapseProjects.includes(project.key) ? <ArrowDropUp /> : <ArrowDropDown />}
							</ListItemButton>
							<Collapse
								id={index}
								in={!collapseProjects.includes(project.key)}
								timeout="auto"
								unmountOnExit
							>
								{project?.forms?.map((form) => {
									return (
										<ListItemButton
											key={form.key}
											onClick={() => handleFormSelection(project, form)}
										>
											<ListItemText>
												<Typography variant={'body1'} fontSize={14}>
													{form.name}
												</Typography>
											</ListItemText>
										</ListItemButton>
									)
								})}
							</Collapse>
						</div>
					))}
				</List>
			</Popover>
		</Box>
	)
}

export default JobSummaryForm
